import React, { useState } from 'react';
import { PanelShell } from '../ui/PanelShell';
import { SectionTitle } from '../ui/SectionTitle';
import { PillButton } from '../ui/PillButton';
type Face = 'front' | 'right' | 'top' | 'left' | 'back' | 'bottom';
const faces: {
  id: Face;
  label: string;
  points: string;
}[] = [{
  id: 'front',
  label: 'Front face down',
  points: '6,10 16,10 16,20 6,20'
}, {
  id: 'right',
  label: 'Right face down',
  points: '16,10 20,6 20,16 16,20'
}, {
  id: 'top',
  label: 'Top face down',
  points: '6,10 10,6 20,6 16,10'
}, {
  id: 'left',
  label: 'Left face down',
  points: '6,10 10,6 10,16 6,20'
}, {
  id: 'back',
  label: 'Back face down',
  points: '10,6 20,6 20,16 10,16'
}, {
  id: 'bottom',
  label: 'Bottom face down',
  points: '6,20 10,16 20,16 16,20'
}];
const axes = [{
  id: 'x',
  label: 'Orient X',
  color: '#d0201b'
}, {
  id: 'y',
  label: 'Orient Y',
  color: '#16a316'
}, {
  id: 'z',
  label: 'Orient Z',
  color: '#0a63c9'
}];
type OrientationPanelProps = {
  onClose: () => void;
  onAutoOrient: () => void;
  busy: boolean;
};
export function OrientationPanel({
  onClose,
  onAutoOrient,
  busy
}: OrientationPanelProps) {
  const [rotations, setRotations] = useState<Record<string, number>>({
    x: 0,
    y: 0,
    z: 0
  });
  const [selectingBase, setSelectingBase] = useState(false);
  const changed = Object.values(rotations).some(r => r !== 0);
  const rotate = (id: string, delta: number) => setRotations(p => ({
    ...p,
    [id]: (p[id] + delta + 360) % 360
  }));
  return <PanelShell title="Orientation" onClose={onClose} footer={<>
          <PillButton variant="primary" disabled={busy} title="Orients for detail and keeps protected surfaces away from supports" onClick={() => {
      setRotations({
        x: 0,
        y: 0,
        z: 0
      });
      onAutoOrient();
    }} data-id="element-427" data-mp="2jtbc6zeest9j">
          
            {busy ? 'Orienting…' : 'Auto-Orient All'}
          </PillButton>
          <PillButton disabled={!changed} onClick={() => setRotations({
      x: 0,
      y: 0,
      z: 0
    })} data-id="element-428" data-mp="211ihn0joyzzp">
            Reset Selected
          </PillButton>
        </>} data-id="element-426" data-mp="2ezp5dnuxfjln">
      
      <SectionTitle info data-id="element-429" data-mp="1uafnluox762">Orient to Face</SectionTitle>
      <PillButton variant="outlineAccent" className={`mb-[2px] w-full ${selectingBase ? 'bg-pf-blue-soft' : ''}`} aria-pressed={selectingBase} onClick={() => setSelectingBase(s => !s)} data-id="element-430" data-mp="1tik2b13f9fs">
        
        {selectingBase ? 'Click a face on the model…' : 'Select Base...'}
      </PillButton>

      <SectionTitle data-id="element-431" data-mp="phs9dmximfbo">Orient Axes</SectionTitle>
      <div className="flex flex-col gap-[6px]" data-id="element-432" data-mp="kkjqfk4jm79d">
        {axes.map(a => <div key={a.id} className="relative flex h-[24px] items-center justify-center rounded-[4px] border border-[#cdd0d4] bg-[#f1f1f3]" data-id="element-433" data-mp="gj6hh165lny8">
            <span className="text-[12.5px]" style={{
          color: a.color
        }} data-id="element-434" data-mp="bj243k0jl9c8">
              {a.label}
              {rotations[a.id] !== 0 && <span className="ml-1 text-pf-muted" data-id="element-435" data-mp="31aqiswxs19x4">{rotations[a.id]}°</span>}
            </span>
            <div className="absolute right-[8px] flex flex-col gap-[3px]" data-id="element-436" data-mp="2w8rhrtpiep3c">
              <button type="button" aria-label={`Rotate ${a.id.toUpperCase()} positive 90°`} onClick={() => rotate(a.id, 90)} className="text-[#9a9ea4] hover:text-pf-ink" data-id="element-437" data-mp="1btctfgkkf7lv">
                <svg viewBox="0 0 8 5" className="h-[4px] w-[7px]" fill="currentColor" data-id="element-438" data-mp="1kt87pu9f3iq4"><path d="M4 0 L8 5 H0 Z" data-id="element-439" data-mp="1kr5b2rdrercf" /></svg>
              </button>
              <button type="button" aria-label={`Rotate ${a.id.toUpperCase()} negative 90°`} onClick={() => rotate(a.id, -90)} className="text-[#9a9ea4] hover:text-pf-ink" data-id="element-440" data-mp="1pl9jzg6l59g5">
                <svg viewBox="0 0 8 5" className="h-[4px] w-[7px]" fill="currentColor" data-id="element-441" data-mp="1zh34blqjq78h"><path d="M0 0 H8 L4 5 Z" data-id="element-442" data-mp="24gz6dffonole" /></svg>
              </button>
            </div>
          </div>)}
      </div>

      <SectionTitle data-id="element-443" data-mp="28iu8un9co80r">Orient to Bounding Box</SectionTitle>
      <div className="grid grid-cols-3 gap-[8px] pb-[2px]" data-id="element-444" data-mp="28g82us23y0kn">
        {faces.map(f => <button key={f.id} type="button" aria-label={f.label} className="flex h-[40px] items-center justify-center rounded-[4px] bg-[#e2e3e5] transition-colors duration-150 hover:bg-[#d6d8db]" data-id="element-445" data-mp="a4ranzvjf3ok">
          
            <svg viewBox="0 0 26 26" className="h-[24px] w-[24px]" fill="none" data-id="element-446" data-mp="3ayp1qpjozcge">
              <polygon points={f.points} fill="#9fcbea" data-id="element-447" data-mp="3oxon9obwrb8c" />
              <path d="M6 10 H16 V20 H6 Z M6 10 L10 6 H20 V16 L16 20 M16 10 L20 6" stroke="#a4a8ad" strokeWidth="0.9" data-id="element-448" data-mp="3oxeyg42lna48" />
              <path d="M10 6 V16 H20 M10 16 L6 20" stroke="#a4a8ad" strokeWidth="0.6" opacity="0.6" data-id="element-449" data-mp="3jx0y7878p4gr" />
            </svg>
          </button>)}
      </div>
    </PanelShell>;
}